import { useRouter } from 'next/router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchContents } from 'src/api/content'
import { useMasterData } from 'src/api/masterData'
import { SearchIcon } from 'src/assets/svgs'
import { useRouteQuery, useRouteQueryArray } from 'src/hooks/useRouteQuery'
import { Pagination } from 'src/components/Pagination'
import { accentColor } from 'src/styles/colors'
import { fontWeightBold } from 'src/styles/font'
import styled from 'styled-components'
import { ContentCard } from './common'

export const ContentSearchPageLayout = () => {
  const router = useRouter()
  const { data: masterData } = useMasterData()
  const { orderBy, filterBy, tag, category, keyword } = router.query
  const [pageIndex, setPageIndex] = useState<number>(
    Number(router.query.page ?? '1'),
  )
  const perPage = Number(router.query.perPage ?? '15')

  const [selectedTags, setSelectedTags] = useRouteQueryArray('tag', [], {
    resetPage: true,
  })
  const [keywordInput, setKeywordInput] = useRouteQuery('keyword', '', {
    debounce: 300,
    resetPage: true,
    serialize: (v) => v.trim() || undefined,
  })

  // ドラッグ&ドロップ機能用のstate
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [scrollLeft, setScrollLeft] = useState(0)

  // SearchTabbarのref
  const searchTabbarRef = useRef<HTMLDivElement>(null)

  // クエリパラメータに基づいてtagIdsを決定
  const effectiveTagIds = tag
    ? Array.isArray(tag)
      ? tag.map((t) => Number(t))
      : [Number(tag)]
    : []

  // useSearchContentsフックを使用してデータを取得
  const { data, isValidating } = useSearchContents(
    effectiveTagIds,
    orderBy as string,
    filterBy as string,
    keyword as string,
    category as string,
    pageIndex,
    perPage,
  )

  // アクティブタブにスクロールする関数
  const scrollToActiveTab = useCallback(() => {
    if (!searchTabbarRef.current) return

    const container = searchTabbarRef.current
    const activeTab = container.querySelector('.active') as HTMLElement

    if (activeTab) {
      const containerWidth = container.offsetWidth
      const activeTabLeft = activeTab.offsetLeft
      const activeTabWidth = activeTab.offsetWidth

      // アクティブタブが中央に来るようにスクロール
      const scrollPosition =
        activeTabLeft - containerWidth / 2 + activeTabWidth / 2

      container.scrollTo({
        left: Math.max(0, scrollPosition),
        behavior: 'smooth',
      })
    }
  }, [])

  // 初期表示時とアクティブタブが変更された時にスクロール
  useEffect(() => {
    // 少し遅延を入れてDOMの更新を待つ
    const timer = setTimeout(scrollToActiveTab, 100)
    return () => clearTimeout(timer)
  }, [orderBy, filterBy, category, tag, scrollToActiveTab])

  // マウスダウンイベントハンドラー
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!searchTabbarRef.current) return

    setIsDragging(true)
    setStartX(e.pageX - searchTabbarRef.current.offsetLeft)
    setScrollLeft(searchTabbarRef.current.scrollLeft)
  }

  // マウスリーブイベントハンドラー
  const handleMouseLeave = () => {
    setIsDragging(false)
  }

  // マウスアップイベントハンドラー
  const handleMouseUp = () => {
    setIsDragging(false)
  }

  // クリックイベントハンドラー（ドラッグ中は無効化）
  const handleTabClickWithDragCheck = (type: string, value?: string) => {
    if (isDragging) return
    handleTabClick(type, value)
  }

  // マウスムーブイベントハンドラー
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !searchTabbarRef.current) return

    e.preventDefault()
    const x = e.pageX - searchTabbarRef.current.offsetLeft
    const walk = (x - startX) * 2 // スクロール速度を調整
    searchTabbarRef.current.scrollLeft = scrollLeft - walk
  }

  // 現在アクティブなカテゴリを取得
  const getActiveCategory = () => {
    if (orderBy === 'recent') return 'recent'
    if (orderBy === 'likes') return 'likes'
    if (filterBy === 'favorite') return 'favorite'
    if (category) return category as string
    if (tag) {
      const selectedTag = masterData?.contentTags?.find((t) => t.value === tag)
      return selectedTag?.category
    }
    return 'recent' // デフォルト
  }

  // カテゴリ別にタグをグループ化
  const getCategoryGroups = () => {
    if (!masterData?.contentTags) return []

    const groups = masterData.contentTags.reduce((acc, tag) => {
      const category = tag.category || 'other'
      if (!acc[category]) acc[category] = { category, tags: [] }
      acc[category].tags.push(tag)
      return acc
    }, {} as Record<string, { category: string; tags: any[] }>)

    // ContentPageLayoutのtagCategoriesの順序に合わせてカテゴリを並び替え
    const categoryOrder = [
      '経営戦略・事業開発',
      'ブランディング・マーケティング',
      'セールス強化・売上拡大',
      '人材採用',
      '人・組織づくり',
      '業務改善・システム開発・デジタル化',
      '財務・資金管理・バックオフィス',
      'その他',
    ]

    const orderedGroups = categoryOrder
      .map((category) => groups[category])
      .filter(Boolean)

    // 順序に含まれていないカテゴリがあれば最後に追加
    const remainingGroups = Object.values(groups).filter(
      (group) => !categoryOrder.includes(group.category),
    )

    return [...orderedGroups, ...remainingGroups]
  }

  const isActive = (type: string, value?: string) => {
    if (type === 'orderBy') return orderBy === value

    if (type === 'filterBy') return filterBy === value
    else if (type === 'tag') return tag === value

    return false
  }

  // アクティブなカテゴリを取得
  const activeCategory = getActiveCategory()

  // マスターデータから、選択したカテゴリーに属するタグをフィルタリング
  const getFilteredTags = () => {
    if (
      activeCategory === 'recent' ||
      activeCategory === 'likes' ||
      activeCategory === 'favorite'
    )
      return []

    // マスターデータから、選択したカテゴリーに属するタグをフィルタリング
    if (masterData?.contentTags && activeCategory) {
      return masterData.contentTags.filter(
        (tag) => tag.category === activeCategory,
      )
    }

    return []
  }

  const handleTabClick = (type: string, value?: string) => {
    const newQuery = { ...router.query }

    if (type === 'orderBy') {
      newQuery.orderBy = value
      // 他のクエリパラメータをクリア
      delete newQuery.filterBy
      delete newQuery.tag
      delete newQuery.category
      // タグ選択はURL変更後にフックが自動でリセット
    } else if (type === 'filterBy') {
      newQuery.filterBy = value
      // 他のクエリパラメータをクリア
      delete newQuery.orderBy
      delete newQuery.tag
      delete newQuery.category
      // タグ選択はURL変更後にフックが自動でリセット
    } else if (type === 'tag') {
      newQuery.tag = value
      // 他のクエリパラメータをクリア
      delete newQuery.orderBy
      delete newQuery.filterBy
      delete newQuery.category
    } else if (type === 'category') {
      newQuery.category = value
      // 他のクエリパラメータをクリア
      delete newQuery.orderBy
      delete newQuery.filterBy
      delete newQuery.tag
      // タグ選択はURL変更後にフックが自動でリセット
    }

    // ページを1にリセットして検索実行
    newQuery.page = '1'
    setPageIndex(1)

    router.push({
      pathname: router.pathname,
      query: newQuery,
    })
  }

  const handleTagToggle = (tagValue: string) => {
    const newSelectedTags = selectedTags.includes(tagValue)
      ? selectedTags.filter((t) => t !== tagValue)
      : [...selectedTags, tagValue]

    setSelectedTags(newSelectedTags)
  }

  const renderSearchConditions = () => {
    return (
      <ContentTop>
        <h2>検索条件</h2>
        <SearchTabbar
          ref={searchTabbarRef}
          onMouseDown={handleMouseDown}
          onMouseLeave={handleMouseLeave}
          onMouseUp={handleMouseUp}
          onMouseMove={handleMouseMove}
          $isDragging={isDragging}
        >
          <SearchTabbarItem
            className={isActive('orderBy', 'recent') ? 'active' : ''}
            onClick={() => handleTabClickWithDragCheck('orderBy', 'recent')}
          >
            新着一覧
          </SearchTabbarItem>
          <SearchTabbarItem
            className={isActive('orderBy', 'likes') ? 'active' : ''}
            onClick={() => handleTabClickWithDragCheck('orderBy', 'likes')}
          >
            人気ランキング一覧
          </SearchTabbarItem>
          <SearchTabbarItem
            className={isActive('filterBy', 'favorite') ? 'active' : ''}
            onClick={() => handleTabClickWithDragCheck('filterBy', 'favorite')}
          >
            お気に入り一覧
          </SearchTabbarItem>
          {getCategoryGroups().map((group) => (
            <SearchTabbarItem
              key={group.category}
              className={getActiveCategory() === group.category ? 'active' : ''}
              onClick={() =>
                handleTabClickWithDragCheck('category', group.category)
              }
            >
              {group.category}
            </SearchTabbarItem>
          ))}
        </SearchTabbar>
        <SearchConditions>
          {getFilteredTags().length > 0 && (
            <SearchConditionTags>
              {getFilteredTags().map((tag) => (
                <SearchConditionTag
                  key={tag.value}
                  className={selectedTags.includes(tag.value) ? 'selected' : ''}
                  onClick={() => handleTagToggle(tag.value)}
                >
                  {tag.label}
                </SearchConditionTag>
              ))}
            </SearchConditionTags>
          )}
          <SearchKeywordInput>
            <input
              type="text"
              placeholder="キーワードを入れて検索"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
            />
            <SearchIcon />
          </SearchKeywordInput>
          <SearchConditionsDetail>
            <div>詳しい条件：</div>
            <span>
              {[
                isActive('orderBy', 'recent') && '新着一覧',
                isActive('orderBy', 'likes') && '人気ランキング一覧',
                isActive('filterBy', 'favorite') && 'お気に入り記事一覧',
                category && category,
                selectedTags.length > 0 &&
                  selectedTags
                    .map(
                      (tagValue) =>
                        masterData?.contentTags?.find(
                          (t) => t.value === tagValue,
                        )?.label,
                    )
                    .join(', '),
                keyword && `キーワード: ${keyword}`,
              ]
                .filter(Boolean)
                .join(', ')}
            </span>
          </SearchConditionsDetail>
        </SearchConditions>
      </ContentTop>
    )
  }

  // APIから取得したデータをそのまま使用（フィルタリングはAPI側で行われる）
  const filteredContents = data?.contents || []
  const filteredTotalCount = data?.totalCount || 0

  // データが取得できない場合はローディング状態
  if (isValidating || !data)
    return (
      <Layout>
        {renderSearchConditions()}
        <ContentNoResult />
      </Layout>
    )

  const { currentPage } = data

  return (
    <>
      <Layout>
        {renderSearchConditions()}

        {filteredContents.length > 0 && (
          <ContentResult>
            <ContentSummary>
              {filteredTotalCount}件中{(currentPage - 1) * perPage + 1}～
              {Math.min(currentPage * perPage, filteredTotalCount)}件表示中
            </ContentSummary>
            <CardsGrid>
              {filteredContents.map((c) => (
                <ContentCard key={c.id} content={c} />
              ))}
            </CardsGrid>
            <Pagination
              itemSize={filteredTotalCount}
              setIndex={(pageIndex) => {
                setPageIndex(pageIndex)
                router.push({
                  pathname: router.pathname,
                  query: { ...router.query, page: pageIndex },
                })
              }}
              forcePage={pageIndex - 1}
              onePageDisplayItems={perPage}
            />
          </ContentResult>
        )}
        {filteredContents.length === 0 && (
          <ContentNoResult>検索結果はありませんでした</ContentNoResult>
        )}
      </Layout>
    </>
  )
}

const Layout = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 40px 20px;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 64px;
`
const ContentTop = styled.div`
  display: flex;
  align-items: left;
  flex-direction: column;
  gap: 24px;
  h2 {
    font-size: 20px;
    font-weight: ${fontWeightBold};
  }
`
const SearchTabbar = styled.div<{ $isDragging: boolean }>`
  display: flex;
  overflow-x: auto;
  scroll-behavior: ${({ $isDragging }) => ($isDragging ? 'auto' : 'smooth')};
  cursor: ${({ $isDragging }) => ($isDragging ? 'grabbing' : 'grab')};
  user-select: none;

  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }

  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  &:active {
    cursor: grabbing;
  }
`
const SearchTabbarItem = styled.div`
  font-size: 13px;
  font-weight: ${fontWeightBold};
  padding: 16px 24px;
  color: #a8a9ac;
  border-bottom: 2px solid #cdcdcd;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  user-select: none;
  flex-shrink: 0;

  &:hover {
    color: #11171f;
    border-color: ${accentColor};
  }

  &.active {
    color: #11171f;
    border-color: ${accentColor};
  }
`
const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 32px;
`
const SearchConditions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
`
const SearchConditionTags = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 8px;
  flex-wrap: wrap;
`
const SearchConditionTag = styled.div`
  font-size: 16px;
  font-weight: ${fontWeightBold};
  padding: 12px 16px;
  background: transparent;
  border-radius: 100px;
  border: 1px solid #e4e4e4;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #effaf6;
    color: ${accentColor};
    border-color: ${accentColor};
  }

  &.selected {
    background: #effaf6;
    color: ${accentColor};
    border-color: ${accentColor};
  }
`
const SearchKeywordInput = styled.div`
  position: relative;
  width: 413px;

  input {
    width: 100%;
    border: 1px solid #808080;
    border-radius: 5px;
    padding: 12px 40px 12px 21px;
    font-size: 16px;
    outline: none;

    &:focus {
      border-color: ${accentColor};
    }

    &::placeholder {
      color: #a8a9ac;
    }
  }

  svg {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 24px;
    height: 24px;
    pointer-events: none;
    color: #a8a9ac;
  }
`
const SearchConditionsDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  div {
    font-size: 14px;
    font-weight: ${fontWeightBold};
  }
  span {
    display: block;
    font-size: 14px;
  }
`
const ContentResult = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`
const ContentSummary = styled.div`
  font-size: 14px;
  font-weight: ${fontWeightBold};
  color: #393e4c;
`
const ContentNoResult = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 240px;
  font-size: 20px;
  font-weight: ${fontWeightBold};
  text-align: center;
  border-radius: 0 64px 0 64px;
  background: #f8f8f8;
`
